import { useEffect, useState, useRef, useMemo } from "react";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { removeCompanyPresetQuery } from "@query";
import { useStores, useCheckEstimateEditorAccess } from "@hooks";
import { BOTTOM_BAR_ID, EDITOR_CONTAINER_ID } from "@utils";
import {
  useEstimatePresetStore,
  useEstimateEditorStore,
  PresetPicker,
  Share,
  NameEditorModal,
} from "@tools";
import { Alert, Button } from "@components";
import { AppBar, Grid, Toolbar } from "@material-ui/core";
import { FontPicker } from "./FontPicker";
import { ToolPopup } from "./ToolPopup/ToolPopup";
import { Tooltip } from "./Tooltip/Tooltip";
import { Trash } from "@assets";
import { Check } from "@material-ui/icons";
import { Exports } from "@tools/EstimateEditor/components/TableToolbar/components";

export const PresetToolbar = observer(() => {
  const presetStore = useEstimatePresetStore();
  const { estimateUuid } = useParams();
  const editorStore = useEstimateEditorStore();
  const { userStore } = useStores();
  const { t } = useTranslation();

  const { estimateUsers } = editorStore;
  const { isManager } = userStore;

  const { isBusinessUser, hasSharePrivileges } =
    useCheckEstimateEditorAccess(estimateUsers);

  const [isLoading, setLoadingState] = useState(true);
  const [isRemoving, setRemovingState] = useState(false);
  const [presetCreateModalVisible, showPresetCreateModal] = useState(false);
  const [currentElement, _setCurrentElement] = useState(null);
  const [currentElementClass, setCurrentElementClass] = useState(null);
  const [openToolMenu, _setToolMenuOpen] = useState(false);
  const [closingAlertVisible, showClosingAlert] = useState(false);
  const [removePresetAlertVisible, showPresetAlertVisible] = useState(false);

  const openToolMenuRef = useRef(openToolMenu);
  const currentElementRef = useRef(currentElement);

  const { selectedPresetId, presets, presetChanged, defaultPresetId } = presetStore;

  const setCurrentElement = (d) => {
    currentElementRef.current = d;
    _setCurrentElement(d);
  };
  const setToolMenuOpen = (d) => {
    openToolMenuRef.current = d;
    _setToolMenuOpen(d);
  };
  
  const setupEvents = (cleanup) => {
    const event = cleanup ? "removeEventListener" : "addEventListener";
    const element = document.getElementById(EDITOR_CONTAINER_ID);
    const bottomBarElement = document.getElementById(BOTTOM_BAR_ID);
    
    element?.[event]("mousemove", lookupPresetElements);
    bottomBarElement?.[event]("mousemove", lookupPresetElements);
    element?.[event]("mousedown", handlePresetElementClick);
    bottomBarElement?.[event]("mousedown", handlePresetElementClick);
  }

  useEffect(() => {
    (async () => {
      setLoadingState(true);
      await presetStore.getPresets(t("views.editor.preset.apropo"));
      setLoadingState(false);
    })();
    
    setupEvents();

    return () => {
      if (presetStore.presetChanged)
        handlePresetCommit(selectedPresetId === -1)();
      setupEvents(true);
    };
  }, []);

  // useEffect(() => {
  //   if (previewMode) {
  //     _setToolMenuOpen(false);
  //     window.document.removeEventListener("mousemove", lookupPresetElements);
  //     window.document.removeEventListener("click", handlePresetElementClick);
  //   } else {
  //     window.document.addEventListener("mousemove", lookupPresetElements);
  //     window.document.addEventListener("click", handlePresetElementClick);
  //   }
  // }, [previewMode]);

  useEffect(() => {
    if (presetChanged && selectedPresetId === -1) showPresetCreateModal(true);
  }, [presetChanged, selectedPresetId]);

  const lookupPresetElements = (e) => {
    if (openToolMenuRef.current) return;
    const el = e.target.closest(getPresetClassesSelector());
    if (
      el &&
      (!currentElementRef?.current ||
        currentElementRef?.current.isVirtual ||
        !el.isEqualNode(currentElementRef.current))
    ) {
      if(typeof el.className === "object" && !el.className.baseVal)
        return;
      
      const className = typeof el.className === "object"
        ? el.className.baseVal
        : el.className
      
      // if class is "preset-page" virtual element should be passed as an element
      const isPage = className.match(/preset-((page)|(paperTimeline)|(paperTextDescription)|(paper)|(bar))/);
      
      
      const getBoundingClientRect = () => ({
        x: e.clientX,
        y: e.clientY,
        top: e.clientY,
        bottom: e.clientY + 10,
        left: e.clientX,
        right: e.clientX + 10,
        width: 10,
        height: 10,
      })
      
      setCurrentElement({
        getBoundingClientRect,
        isVirtual: true,
        textClass: className.match(/pt-\w+/)?.[0],
        elClass: isPage
          ? isPage[0]
          : className.match(/preset-\w+(-\w+)?/)?.[0],
        clientWidth: getBoundingClientRect().width,
        clientHeight: getBoundingClientRect().height,
      });
      setCurrentElementClass(
        isPage
          ? isPage[0]
          : className.match(/preset-\w+(-\w+)?/)?.[0]
      )
    }
  };

  const handlePresetElementClick = (e) => {
    if (openToolMenuRef.current || e?.target?.closest("[role='presentation']"))
      return;
    const el = e.target.closest(getPresetClassesSelector());
    if (
      currentElementRef?.current?.isVirtual ||
      (el &&
        currentElementRef?.current &&
        el.isEqualNode(currentElementRef.current))
    ) {
      e.preventDefault();
      e.stopPropagation();
      setToolMenuOpen(true);
    }
  };

  const handleNewPresetModalClose = async (newName) => {
    try {
      if (newName) {
        await presetStore.createPreset(newName);
        await presetStore.addPresetToProject(estimateUuid);
      } else if (selectedPresetId === -1) presetStore.resetDefaultPreset();
      showPresetCreateModal(false);
    } catch(e) {
      showPresetCreateModal(false);
    }
  };

  const saveAndClose = () => {
    handlePresetCommit(selectedPresetId === -1)();
    showClosingAlert(false);
    editorStore.setProposalStep(0);
  };

  const closePresetEditor = () => {
    showClosingAlert(false);
    editorStore.setProposalStep(0);
    const n = presets.find(({ id }) => id === selectedPresetId);
    presetStore.setSelectedPreset(selectedPresetId, n?.name, n ? n.data : null, n.font?.id ? n.font : undefined);
  };
  
  const handleDefaultPreset = async () => {
    await presetStore.setDefaultPresetId(selectedPresetId);
  }

  const handlePresetCommit =
    (isNew = false) =>
    async () => {
      if (!isNew) await presetStore.updatePreset();
      else showPresetCreateModal(true);
    };
  
  const handlePresetRemove = async () => {
    setRemovingState(true);
    const presetId = selectedPresetId;
    const p = presets.find(({ id }) => id === -1);
    await presetStore.setSelectedPreset(p.id, p.name, p.data);
    await presetStore.addPresetToProject(estimateUuid);
    if(presetId === presetStore.defaultPresetId)
    await presetStore.setDefaultPresetId(-1);
    
    await removeCompanyPresetQuery(presetId);
    await presetStore.removePreset(presetId);
    showPresetAlertVisible(false);
    setRemovingState(false);
  }
  
  const isDefault = useMemo(() => (
    Number(defaultPresetId) === Number(selectedPresetId)
  ), [defaultPresetId, selectedPresetId]);
  
  if (isLoading) return null;

  return (
    <AppBar
      position="static"
      role="banner"
      color="secondary"
      style={{ zIndex: 10 }}
    >
      <Toolbar style={{ justifyContent: "space-between" }}>
        <Grid item container wrap="nowrap" alignItems="center">
          <PresetPicker
            isLoading={isLoading}
            commitPresetSelection
            className="mr-2"
          />
          <FontPicker />
          <Button
            variant="outlined"
            onClick={handlePresetCommit(true)}
            style={{ maxWidth: 136 }}
            className="mr-2"
          >
            {selectedPresetId === -1
              ? t("common.add_new")
              : t("views.editor.preset.save_as")}
          </Button>
          {
            !isDefault && isManager &&
            <Button
              size="medium"
              variant="outlined"
              color="primary"
              className="mr-2"
              onClick={handleDefaultPreset}
            >
              {t("common.default_set")}
            </Button>
          }
          {
            selectedPresetId > 0 &&
            <Button
              square
              size="medium"
              variant="outlined"
              className="mr-2"
              icon={
                <Trash className="color-error text-md" />
              }
              onClick={() => showPresetAlertVisible(true)}
              color="secondary"
              name={t("views.editor.preset.remove")}
              tooltip
            />
          }
          {
            isDefault &&
            <Grid item container wrap="nowrap" alignItems="center" className="w-max text-xs color-primary">
              <Check className="mr-1 text-xl" />
              {t("views.editor.preset.default")}
            </Grid>
          }
        </Grid>
        <Grid
          item
          container
          justifyContent="flex-end"
          wrap="nowrap"
          className="w-max"
          spacing={1}
        >
          {
            hasSharePrivileges &&
            <Grid item container className="w-max">
              <Exports size="medium" />
            </Grid>
          }
          {
            isBusinessUser &&
            <Grid item container className="w-max">
              <Share usePresetModeButton size="medium" />
            </Grid>
          }
        </Grid>
        <ToolPopup
          show={openToolMenu}
          anchor={currentElement}
          // presetName={selectedPresetName}
          isDefaultPreset={selectedPresetId === -1}
          onClose={() => setToolMenuOpen(false)}
        />
        <Tooltip
          show={!openToolMenu && !presetCreateModalVisible}
          // anchor={currentHelperElement}
          elementClass={currentElementClass}
        />
        <NameEditorModal
          open={presetCreateModalVisible}
          onClose={handleNewPresetModalClose}
          title={t("views.editor.preset.name")}
          confirmButtonTitle={t("views.editor.preset.create_new")}
        />
        <Alert
          title={t("views.editor.preset.alert.title")}
          isOpen={closingAlertVisible}
          onAccept={saveAndClose}
          acceptText={t("views.editor.preset.alert.accept")}
          onCancel={closePresetEditor}
          cancelText={t("common.close")}
        />
        <Alert
          title={t("views.editor.preset.alert_remove")}
          isOpen={removePresetAlertVisible}
          onAccept={handlePresetRemove}
          onCancel={() => showPresetAlertVisible(false)}
          isAcceptLoading={isRemoving}
          acceptText={t("common.confirm_remove")}
          cancelText={t("common.cancel_remove")}
        />
      </Toolbar>
    </AppBar>
  );
});

const getPresetClassesSelector = () => {
  const classes = [
    "preset-title",
    "preset-logo",
    "preset-bar",
    "preset-total",
    "preset-scrollButton",
    "preset-bottomBarButton",
    "preset-bottomBarOpenButton",
    "preset-commentsSidebarButton",
    "preset-showPriceForIcon",
    "preset-showPriceFor",
    "preset-textSearchButton",
    "preset-labelFilterButton",
    "preset-summaryTitleText",
    "preset-summaryText",
    "preset-timelineTitleText",
    "preset-timelineText",
    "preset-titleTextSection",
    "preset-titleText",
    "preset-text",
    "preset-version-active",
    "preset-version",
    "preset-timelineMode",
    "preset-timelineModeActive",
    "preset-moreDesc",
    "preset-moreDescSection",
    "preset-sectionDesc",
    "preset-tableBreakdownExpandButton",
    "preset-tableSummaryExpandButton",
    "preset-tableTimelineExpandButton",
    "preset-textDescriptionExpandButton",
    "preset-breakdownExpandButton",
    "preset-section",
    "preset-sectionRow",
    "preset-timelineSubHeader",
    "preset-timelineHeader",
    "preset-tableBreakdownHeader",
    "preset-descriptionHeader",
    "preset-tableTimelineHeader",
    "preset-tableSummaryHeader",
    "preset-tableTimelineBilling",
    "preset-textDescriptionContent",
    "preset-tableFooter",
    "preset-summaryHeader",
    "preset-textDescriptionHeader",
    "preset-totalTimeTitle",
    "preset-totalHoursTitle",
    "preset-totalCostTitle",
    "preset-totalTime",
    "preset-totalHours",
    "preset-totalCost",
    "preset-breakdownHeader",
    "preset-price",
    "preset-active-wT",
    "preset-active-1",
    "preset-active-2",
    "preset-activeSummary",
    "preset-activeTimeline",
    "preset-rowSummary",
    "preset-rowTimeline",
    "preset-rowDesc",
    "preset-row",
    "preset-export",
    "preset-paperTextDescription",
    "preset-paperTimeline",
    "preset-paper",
    "preset-page:not(.preset-noedit)",
  ];
  return classes.reduce((p, c) => p + ", ." + c, "").slice(1);
};
